import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  MapPin, Phone, Mail, Clock, Target, Users, Award,
  CheckCircle, ArrowRight, Building2
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const AboutPage = () => {
  const { t } = useLanguage();

  const methodology = [
    { step: "01", title: t('etape1'), description: t('etape1Desc') },
    { step: "02", title: t('etape2'), description: t('etape2Desc') },
    { step: "03", title: t('etape3'), description: t('etape3Desc') },
    { step: "04", title: t('etape4'), description: t('etape4Desc') },
    { step: "05", title: t('etape5'), description: t('etape5Desc') }
  ];

  const strengths = [
    t('atout1'), t('atout2'), t('atout3'), t('atout4'), t('atout5'), t('atout6')
  ];

  const coverage = [
    { region: t('bruxellesCapitale'), description: t('bruxellesDesc') },
    { region: t('wallonie'), description: t('wallonieDesc') },
    { region: t('flandre'), description: t('flandreDesc') }
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      <SEO
        title="À propos de Nettis Facility"
        description="Découvrez Nettis Facility, société de nettoyage professionnel en Belgique. Notre méthodologie, nos équipes qualifiées et notre couverture nationale : Bruxelles, Wallonie, Flandre."
        keywords="à propos Nettis, société nettoyage Belgique, entreprise nettoyage Bruxelles, qui sommes nous Nettis Facility"
        canonicalUrl="https://www.nettis.be/a-propos"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-[#1E3A8A] uppercase tracking-widest mb-4 block">{t('aPropos')}</span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
                {t('aProposTitle')} <span className="text-[#1E3A8A]">{t('qualiteEt')}</span> et la <span className="text-[#10B981]">{t('fiabilite')}</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{t('aProposDesc')}</p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div><p className="font-bold text-[#0F172A]">15+</p><p className="text-sm text-slate-500">{t('annees')}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div><p className="font-bold text-[#0F172A]">200</p><p className="text-sm text-slate-500">{t('clients')}</p></div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800" alt="Équipe Nettis" className="rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -right-6 bg-[#1E3A8A] text-white rounded-xl p-6 shadow-lg">
                <p className="text-3xl font-bold mb-1">100%</p>
                <p className="text-sm text-blue-100">{t('satisfaction')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('notreVision')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">{t('redefinirStandards')}</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{t('missionDesc')}</p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] flex items-center justify-center shrink-0">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">{t('notreMission')}</h3>
                    <p className="text-sm text-slate-600">{t('notreMissionDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#10B981] flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">{t('notreVisionTitle')}</h3>
                    <p className="text-sm text-slate-600">{t('notreVisionDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-[#1E3A8A] uppercase tracking-widest mb-4 block">{t('nosAtouts')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">{t('pourquoiNousChoisir')}</h2>
              <div className="grid grid-cols-1 gap-4">
                {strengths.map((strength, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0" />
                    <span className="text-slate-700">{strength}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#1E3A8A] uppercase tracking-widest mb-4 block">{t('notreApproche')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('notreMethodologie')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('methodologieDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {methodology.map((method, index) => (
              <div key={method.step} className="relative bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <span className="text-4xl font-bold text-[#1E3A8A]/10">{method.step}</span>
                <h3 className="text-lg font-bold text-[#0F172A] mt-2 mb-2">{method.title}</h3>
                <p className="text-sm text-slate-600">{method.description}</p>
                {index < methodology.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('zoneIntervention')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">{t('couvertureNationale')}</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{t('couvertureDesc')}</p>
              <div className="space-y-4">
                {coverage.map((area, index) => (
                  <div key={area.region} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <MapPin className="w-6 h-6 text-[#1E3A8A] shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#0F172A]">{area.region}</h3>
                      <p className="text-sm text-slate-500">{area.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#1E3A8A] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">{t('nosCoordonnees')}</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div><p className="font-medium mb-1">{t('adresse')}</p><p className="text-blue-100">Avenue Louise 65 boîte 11<br />1050 Bruxelles, {t('belgique')}</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div><p className="font-medium mb-1">{t('telephone')}</p><a href="tel:+3228806650" className="text-blue-100 hover:text-white transition-colors">+32 2 880 66 50</a></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div><p className="font-medium mb-1">{t('email')}</p><a href="mailto:info@nettis.be" className="text-blue-100 hover:text-white transition-colors">info@nettis.be</a></div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/20">
                <p className="text-sm text-blue-200">TVA: BE0788.309.595</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('pretTravailler')}</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t('pretTravaillerDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-green-900/20 transition-transform duration-200 hover:-translate-y-0.5">
                {t('nousContacter')}
              </Button>
            </Link>
            <Link to="/postuler#formulaire">
              <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:border-[#1E3A8A] hover:text-[#1E3A8A] rounded-full px-8 py-3 font-medium bg-white">
                {t('rejoindreEquipe')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
